import { ConfirmationDialog } from "@capacit-com/lara-law-shared-ui";
import { Button } from "@capacit-com/lara-law-shared-ui";
import { Spinner } from "@capacit-com/lara-law-shared-ui";
import { TooltipProvider } from "@capacit-com/lara-law-shared-ui";
import useSearch from "@/hooks/useSearch";
import { cn } from "@/lib/utils";
import { Search } from "@/models/search/Search";
import { ChevronRight, ArrowLeft } from "lucide-react";
import { useState, useRef, useLayoutEffect } from "react";
import GlobalSearchSearchItem from "./GlobalSearchSearchItem";

interface GlobalSearchInitialViewProps {
  onSearchSelect: (search: Search) => void;
}

const GlobalSearchInitialView = ({
  onSearchSelect,
}: GlobalSearchInitialViewProps) => {
  const {
    useMyRecentSearches,
    useMyFavoriteSearches,
    favoriteSearch,
    unfavoriteSearch,
    deleteSearch,
    deleteAllNonFavoriteSearches,
    isAnyFavoritePending,
    isDeletePending,
    transformStoredSearch,
  } = useSearch();

  const recentSearchesQuery = useMyRecentSearches();
  const favoriteSearchesQuery = useMyFavoriteSearches();

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [isClearAllDialogOpen, setIsClearAllDialogOpen] = useState(false);
  const [isRecentExpanded, setIsRecentExpanded] = useState(false);
  const [isFavoritesExpanded, setIsFavoritesExpanded] = useState(false);

  // Refs for section containers to measure height
  const recentSectionRef = useRef<HTMLDivElement>(null);
  const favoritesSectionRef = useRef<HTMLDivElement>(null);
  const [maxVisibleRecent, setMaxVisibleRecent] = useState(5);
  const [maxVisibleFavorites, setMaxVisibleFavorites] = useState(5);

  // Constants for height calculation
  const HEADER_HEIGHT = 40; // approx height of section header
  const ITEM_HEIGHT = 52; // approx height of each search item
  const BUTTON_HEIGHT = 36; // approx height of "Se flere" button

  // Calculate visible items based on container height
  useLayoutEffect(() => {
    const calculateVisibleItems = () => {
      if (recentSectionRef.current) {
        const containerHeight = recentSectionRef.current.clientHeight;
        const availableHeight = containerHeight - HEADER_HEIGHT - BUTTON_HEIGHT;
        const itemsThatFit = Math.max(
          1,
          Math.floor(availableHeight / ITEM_HEIGHT)
        );
        setMaxVisibleRecent(itemsThatFit);
      }
      if (favoritesSectionRef.current) {
        const containerHeight = favoritesSectionRef.current.clientHeight;
        const availableHeight = containerHeight - HEADER_HEIGHT - BUTTON_HEIGHT;
        const itemsThatFit = Math.max(
          1,
          Math.floor(availableHeight / ITEM_HEIGHT)
        );
        setMaxVisibleFavorites(itemsThatFit);
      }
    };

    calculateVisibleItems();

    const resizeObserver = new ResizeObserver(calculateVisibleItems);
    if (recentSectionRef.current)
      resizeObserver.observe(recentSectionRef.current);
    if (favoritesSectionRef.current)
      resizeObserver.observe(favoritesSectionRef.current);

    return () => resizeObserver.disconnect();
  }, []);

  const recentSearches =
    recentSearchesQuery.data?.map(transformStoredSearch) || [];
  const favoriteSearches =
    favoriteSearchesQuery.data?.searches?.map(transformStoredSearch) || [];

  const isLoadingRecent = recentSearchesQuery.isPending;
  const isLoadingFavorites = favoriteSearchesQuery.isPending;

  const handleDeleteSearch = (search: Search, isFavorite: boolean) => {
    if (isFavorite) {
      setIsDeleteDialogOpen(true);
      setConfirmDeleteId(search.id!);
    } else {
      deleteSearch.mutate(search.id!);
    }
  };

  const handleToggleFavorite = (search: Search, isFavorite: boolean) => {
    if (isFavorite) {
      const unfavoriteId = search.favoriteLinkId || search.id!;
      unfavoriteSearch.mutate(unfavoriteId);
    } else {
      favoriteSearch.mutate(search.id!);
    }
  };

  const renderSearchList = (
    searches: Search[],
    isLoading: boolean,
    emptyMessage: string,
    isFavoriteSection: boolean,
    isExpanded: boolean,
    maxItems: number = 5
  ): { content: React.ReactNode; hasMore: boolean } => {
    if (isLoading) {
      return {
        content: (
          <div className="flex items-center justify-center py-4">
            <div className="text-sm text-muted-foreground">
              Indlæser søgninger...
            </div>
          </div>
        ),
        hasMore: false,
      };
    }

    if (!searches || searches.length === 0) {
      return {
        content: (
          <div className="flex items-center justify-center py-4">
            <div className="text-sm text-muted-foreground">{emptyMessage}</div>
          </div>
        ),
        hasMore: false,
      };
    }

    const displaySearches = isExpanded ? searches : searches.slice(0, maxItems);
    const hasMore = searches.length > maxItems && !isExpanded;

    return {
      content: (
        <div className="flex flex-col gap-0.5">
          {displaySearches.map((search) => {
            const isFavorite = isFavoriteSection
              ? true
              : search.favoriteLinkId !== null &&
                search.favoriteLinkId !== undefined;

            const favoriteSearchId =
              !isFavoriteSection && search.favoriteLinkId
                ? search.favoriteLinkId
                : search.id!;

            const isFavoriteBusy =
              isAnyFavoritePending(favoriteSearchId) ||
              (!isFavoriteSection && search.id
                ? isAnyFavoritePending(search.id)
                : false);

            return (
              <GlobalSearchSearchItem
                key={search.id}
                search={search}
                isFavorite={isFavorite}
                onSelect={() => onSearchSelect(search)}
                onDelete={() => handleDeleteSearch(search, isFavorite)}
                onToggleFavorite={() =>
                  handleToggleFavorite(search, isFavorite)
                }
                isDeleteBusy={isDeletePending(search.id!)}
                isFavoriteBusy={isFavoriteBusy}
              />
            );
          })}
        </div>
      ),
      hasMore,
    };
  };

  // Helper to render the "Se flere" button
  const renderShowMoreButton = (onClick: () => void) => (
    <button
      onClick={onClick}
      className={cn(
        "w-full flex items-center justify-center gap-1 py-2 px-3",
        "text-sm text-primary hover:text-primary/80 hover:bg-accent rounded-sm",
        "transition-colors cursor-pointer"
      )}
    >
      <span>Se flere søgninger</span>
      <ChevronRight className="w-4 h-4" />
    </button>
  );

  return (
    <TooltipProvider>
      <div className="flex flex-col h-full">
        {isRecentExpanded ? (
          // Full height recent section
          <div className="h-full flex flex-col">
            <div className="flex items-center justify-between px-4 py-2 shrink-0 border-b border-border">
              <p className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                <button
                  onClick={() => setIsRecentExpanded(false)}
                  className="p-1 -ml-1 hover:bg-accent rounded-sm transition-colors"
                  title="Tilbage"
                >
                  <ArrowLeft className="w-4 h-4" />
                </button>
                Seneste søgninger
              </p>
              <Button
                variant="ghost"
                size="sm"
                className="text-xs py-1 px-2 h-6"
                onClick={() => setIsClearAllDialogOpen(true)}
                disabled={
                  recentSearches.length === 0 ||
                  deleteAllNonFavoriteSearches.isPending
                }
              >
                {deleteAllNonFavoriteSearches.isPending ? (
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">Rydder...</span>
                    <Spinner className="w-3 h-3" />
                  </div>
                ) : (
                  "Ryd søgninger"
                )}
              </Button>
            </div>
            <div className="px-2 pb-2 flex-1 overflow-y-auto">
              {
                renderSearchList(
                  recentSearches,
                  isLoadingRecent,
                  "Ingen seneste søgninger",
                  false,
                  true
                ).content
              }
            </div>
          </div>
        ) : isFavoritesExpanded ? (
          // Full height favorites section
          <div className="h-full flex flex-col">
            <div className="flex items-center justify-between px-4 py-2 shrink-0 border-b border-border">
              <p className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                <button
                  onClick={() => setIsFavoritesExpanded(false)}
                  className="p-1 -ml-1 hover:bg-accent rounded-sm transition-colors"
                  title="Tilbage"
                >
                  <ArrowLeft className="w-4 h-4" />
                </button>
                Favoriserede søgninger
              </p>
            </div>
            <div className="px-2 pb-2 flex-1 overflow-y-auto">
              {
                renderSearchList(
                  favoriteSearches,
                  isLoadingFavorites,
                  "Ingen favoritsøgninger",
                  true,
                  true
                ).content
              }
            </div>
          </div>
        ) : (
          // 50/50 split (default view)
          (() => {
            const recentResult = renderSearchList(
              recentSearches,
              isLoadingRecent,
              "Ingen seneste søgninger",
              false,
              false,
              maxVisibleRecent
            );
            const favoritesResult = renderSearchList(
              favoriteSearches,
              isLoadingFavorites,
              "Ingen favoritsøgninger",
              true,
              false,
              maxVisibleFavorites
            );

            return (
              <>
                {/* Recent Searches Section - 50% */}
                <div
                  ref={recentSectionRef}
                  className="flex-1 min-h-0 flex flex-col border-b border-border overflow-hidden"
                >
                  <div className="flex items-center justify-between px-4 py-2 shrink-0">
                    <p className="text-sm font-medium text-muted-foreground">
                      Seneste søgninger
                    </p>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-xs py-1 px-2 h-6"
                      onClick={() => setIsClearAllDialogOpen(true)}
                      disabled={
                        recentSearches.length === 0 ||
                        deleteAllNonFavoriteSearches.isPending
                      }
                    >
                      {deleteAllNonFavoriteSearches.isPending ? (
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground">
                            Rydder...
                          </span>
                          <Spinner className="w-3 h-3" />
                        </div>
                      ) : (
                        "Ryd søgninger"
                      )}
                    </Button>
                  </div>
                  {/* Items container - clips overflow */}
                  <div className="px-2 flex-1 overflow-hidden">
                    {recentResult.content}
                  </div>
                  {/* Button - always visible at bottom */}
                  {recentResult.hasMore && (
                    <div className="px-2 pb-2 shrink-0">
                      {renderShowMoreButton(() => setIsRecentExpanded(true))}
                    </div>
                  )}
                </div>

                {/* Favorite Searches Section - 50% */}
                <div
                  ref={favoritesSectionRef}
                  className="flex-1 min-h-0 flex flex-col overflow-hidden"
                >
                  <div className="flex items-center justify-between px-4 py-2 shrink-0">
                    <p className="text-sm font-medium text-muted-foreground">
                      Favoriserede søgninger
                    </p>
                  </div>
                  {/* Items container - clips overflow */}
                  <div className="px-2 flex-1 overflow-hidden">
                    {favoritesResult.content}
                  </div>
                  {/* Button - always visible at bottom */}
                  {favoritesResult.hasMore && (
                    <div className="px-2 pb-2 shrink-0">
                      {renderShowMoreButton(() => setIsFavoritesExpanded(true))}
                    </div>
                  )}
                </div>
              </>
            );
          })()
        )}
      </div>

      {/* Delete single search confirmation */}
      <ConfirmationDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        title="Slet søgning"
        description="Er du sikker på, at du vil slette denne søgning?"
        confirmText="Slet"
        onConfirm={() => {
          if (confirmDeleteId) {
            deleteSearch.mutate(confirmDeleteId);
          }
          setIsDeleteDialogOpen(false);
          setConfirmDeleteId(null);
        }}
        confirmPending={
          confirmDeleteId ? isDeletePending(confirmDeleteId) : false
        }
        variant="destructive"
      />

      {/* Clear all non-favorite searches confirmation */}
      <ConfirmationDialog
        isOpen={isClearAllDialogOpen}
        onClose={() => setIsClearAllDialogOpen(false)}
        onConfirm={() => {
          deleteAllNonFavoriteSearches.mutate();
          setIsClearAllDialogOpen(false);
        }}
        title="Ryd søgninger"
        description="Er du sikker på, at du vil slette alle dine søgninger? <br /> Dette gælder ikke dine favorittiserede søgninger."
        confirmText="Ryd søgninger"
        variant="destructive"
        confirmPending={deleteAllNonFavoriteSearches.isPending}
      />
    </TooltipProvider>
  );
};

export default GlobalSearchInitialView;
